import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Button, Row, Col, Table, Input, Label } from "reactstrap";
import { useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import { hoyLocal } from "../../../fechaLocal";

// Genera PO a partir de un pedido de cliente.
// Se ve el desglose por articulo/color/talla, se ajustan cantidades y se asigna
// un proveedor a cada renglon; al confirmar sale UNA OC por proveedor, y todas
// quedan ligadas al pedido (OrdenesCompra.pedidos).
function GenerarOCPedido() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const URL_ARTICULOS_PEDIDO = process.env.REACT_APP_URL_ARTICULOS_PEDIDO;
  const URL_PROVEEDORES = process.env.REACT_APP_URL_PROVEEDORES;
  const URL_ORDENES_COMPRA = process.env.REACT_APP_URL_ORDENES_COMPRA;
  const URL_ARTICULOS_OC = process.env.REACT_APP_URL_ARTICULOS_OC;
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;
  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  const [renglones, setRenglones] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [encabezado, setEncabezado] = useState({ numero: "", cliente: "" });
  const [provTodos, setProvTodos] = useState("");
  const [plazo, setPlazo] = useState(0);
  const [moneda, setMoneda] = useState("USD");
  const [fechaCompromiso, setFechaCompromiso] = useState("");
  const [generando, setGenerando] = useState(false);
  // articulos abiertos: al tocar la cantidad se abre el modelo para repartirla
  const [abiertos, setAbiertos] = useState({});

  useEffect(() => {
    Promise.all([axios.get(`${URL_ARTICULOS_PEDIDO}/pedidos/${id}`, { headers }), axios.get(URL_PROVEEDORES, { headers })])
      .then(([res, resProv]) => {
        const catalogo = (resProv.data || []).filter((p) => p.is_active !== "No");
        setProveedores(catalogo);
        const data = (res.data || []).filter((a) => a.is_active === "Si");
        const cab = data[0] && (Array.isArray(data[0].pedidos) ? data[0].pedidos[0] : data[0].pedidos);
        if (cab) {
          setEncabezado({
            numero: cab.idPedido,
            cliente: cab.clientes?.[0]?.nombre_comercial || cab.clientes?.nombre_comercial || "",
          });
        }
        // lo que subio el Excel del prepedido: el proveedor del modelo (el "NA" es no
        // tener proveedor) y COSTO FOB o, si vino vacio, COSTO DLLS, que el layout
        // deja en costoMX del modelo; en un modelo capturado a mano ese campo es pesos
        const proveedorDe = (art) => {
          const ref = art.proveedores?.[0];
          const prov = catalogo.find((p) => String(p._id) === String(ref?._id || ref));
          return prov && prov.nombre_comercial !== "NA" ? prov._id : "";
        };
        const costoDe = (a) => {
          const art = a.articulos[0];
          const delLayout = /^IMPORT PREPEDIDO/.test(art.observaciones || "") ? Number(art.costoMX) : 0;
          return Number(a.costoFOB) || Number(art.costoUSD) || delLayout || 0;
        };
        const lineas = data.filter((a) => a.articulos?.[0]?._id).map((a) => ({
          _id: a._id,
          articulos: a.articulos?.[0]?._id,
          // el mismo modelo puede venir en dos renglones del pedido: se agrupa por
          // modelo + renglon para no juntarlos
          renglon: a.renglon || 1,
          grupo: `${a.articulos?.[0]?._id}|${a.renglon || 1}`,
          articulo: `${a.articulos?.[0]?.codigo || ""} ${a.articulos?.[0]?.nombre || ""}`.trim(),
          colores: a.colores?.[0]?._id || "",
          color: a.colores?.[0]?.name || "",
          tallas: a.tallas?.[0]?._id || "",
          talla: a.tallas?.[0]?.name || "",
          pedida: a.cantidad || 0,
          // por omision se compra lo mismo que se pidio
          cantidad: a.cantidad || 0,
          // FECHA FOB (o ENTREGA INICIAL) como fecha compromiso; todo se puede cambiar
          precio: costoDe(a),
          proveedor: proveedorDe(a.articulos[0]),
          // la fecha compromiso es por articulo; se copia a cada variante
          compromiso: a.fechaFOB || a.entregaInicial || "",
        }));
        setRenglones(lineas);
        // arriba: la fecha mas proxima y el proveedor, si todos traen el mismo
        setFechaCompromiso(lineas.map((r) => r.compromiso).filter(Boolean).sort()[0] || "");
        const provs = [...new Set(lineas.map((r) => String(r.proveedor)))];
        setProvTodos(provs.length === 1 ? provs[0] : "");
      })
      .catch(() => Swal.fire({ icon: "error", title: "Ups...", text: "No se pudieron cargar los renglones del pedido" }));
  }, []); // eslint-disable-line

  const cambia = (idRen, campo, valor) =>
    setRenglones((prev) => prev.map((r) => (r._id === idRen ? { ...r, [campo]: valor } : r)));

  // Lo que se cambia en el renglon del articulo baja a todas sus variantes
  const cambiaArticulo = (grupo, campo, valor) =>
    setRenglones((prev) => prev.map((r) => (r.grupo === grupo ? { ...r, [campo]: valor } : r)));

  const asignaATodos = (idProv) => {
    setProvTodos(idProv);
    setRenglones((prev) => prev.map((r) => ({ ...r, proveedor: idProv })));
  };

  // Una fecha para todos: se elige una y se copia a cada articulo (se puede cambiar)
  const compromisoATodos = (fecha) => {
    setFechaCompromiso(fecha);
    setRenglones((prev) => prev.map((r) => ({ ...r, compromiso: fecha })));
  };

  // La captura va por articulo; sus variantes (color y talla) se abren debajo
  const porArticulo = renglones.reduce((acc, r) => {
    if (!acc[r.grupo]) acc[r.grupo] = { grupo: r.grupo, articulos: r.articulos, renglon: r.renglon, articulo: r.articulo, variantes: [] };
    acc[r.grupo].variantes.push(r);
    return acc;
  }, {});
  const repetidos = Object.values(porArticulo).reduce((acc, a) => ({ ...acc, [a.articulos]: (acc[a.articulos] || 0) + 1 }), {});
  const articulos = Object.values(porArticulo).sort((x, y) => x.renglon - y.renglon).map((a) => ({
    ...a,
    pedida: a.variantes.reduce((s, r) => s + Number(r.pedida || 0), 0),
    cantidad: a.variantes.reduce((s, r) => s + Number(r.cantidad || 0), 0),
    precio: a.variantes[0]?.precio || 0,
    proveedor: a.variantes[0]?.proveedor || "",
    compromiso: a.variantes[0]?.compromiso || "",
  }));

  // Solo entran los renglones con proveedor y cantidad; agrupados por proveedor
  const porProveedor = renglones
    .filter((r) => r.proveedor && Number(r.cantidad) > 0)
    .reduce((acc, r) => {
      (acc[r.proveedor] = acc[r.proveedor] || []).push(r);
      return acc;
    }, {});
  const nOCs = Object.keys(porProveedor).length;
  const nRenglones = Object.values(porProveedor).reduce((s, a) => s + a.length, 0);
  const totalPiezas = Object.values(porProveedor).flat().reduce((s, r) => s + Number(r.cantidad || 0), 0);

  async function generar() {
    if (nOCs === 0) {
      Swal.fire({ icon: "warning", title: "Falta proveedor", text: "Asigna proveedor y cantidad a por lo menos un renglon." });
      return;
    }
    const confirma = await Swal.fire({
      title: `Se generaran ${nOCs} orden(es) de compra`,
      html: `${nRenglones} renglon(es) · ${new Intl.NumberFormat("es-MX").format(totalPiezas)} piezas`,
      icon: "question", showCancelButton: true,
      confirmButtonText: "Si, generar", cancelButtonText: "Cancelar",
    });
    if (!confirma.isConfirmed) return;

    setGenerando(true);
    try {
      const folios = [];
      for (const [idProv, lineas] of Object.entries(porProveedor)) {
        const total_cantidad = lineas.reduce((s, r) => s + Number(r.cantidad || 0), 0);
        const total_general = lineas.reduce((s, r) => s + Number(r.cantidad || 0) * Number(r.precio || 0), 0);
        const oc = await axios.post(URL_ORDENES_COMPRA, {
          fecha: hoyLocal(),
          proveedores: idProv,
          pedidos: id,
          plazo: Number(plazo) || 0,
          // el encabezado toma la fecha mas proxima de sus renglones
          fecha_compromiso: lineas.map((r) => r.compromiso).filter(Boolean).sort()[0]
            || fechaCompromiso || hoyLocal(),
          total_general,
          total_cantidad,
          observaciones: `Generada del pedido ${encabezado.numero}`,
          embarcado: 0,
          moneda,
        }, { headers });

        // consecutivo de cada modelo dentro de esta OC (siguiendo el orden del pedido)
        const gruposOC = [...new Set([...lineas].sort((x, y) => x.renglon - y.renglon).map((r) => r.grupo))];
        for (const r of lineas) {
          await axios.post(URL_ARTICULOS_OC, {
            ordenesCompra: oc.data._id,
            articulosPedido: r._id,
            renglon: gruposOC.indexOf(r.grupo) + 1,
            articulos: r.articulos,
            colores: r.colores,
            tallas: r.tallas,
            cantidad: Number(r.cantidad),
            embarcado: 0,
            pendiente_embarcar: Number(r.cantidad),
            precio: Number(r.precio) || 0,
            total: Number(r.cantidad) * (Number(r.precio) || 0),
            // la fecha compromiso de ese articulo
            deliveryDate: r.compromiso || "",
          }, { headers });
        }
        folios.push(oc.data.idOrdenCompra);
      }

      await axios.post(URL_LOGS, {
        tipo: "Generar PO desde Pedido",
        detalle: `Pedido ${encabezado.numero} -> ${nOCs} OC (${folios.join(", ")})`,
        user: user.id,
      }, { headers }).catch(() => {});

      setGenerando(false);
      await Swal.fire({
        icon: "success", title: "Listo",
        html: `Se generaron las PO: <b>${folios.join(", ")}</b>`,
      });
      window.location.href = "/ListadoOrdenesCompra";
    } catch (error) {
      setGenerando(false);
      Swal.fire({
        icon: "error", title: "Ups...", text: "No se pudieron generar las ordenes",
        footer: `${error.response?.data?.message || error.response?.data || error.message}`,
      });
    }
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_oc ? (
        <div className="card container col-12">
          <Row className="mb-3">
            <Col md={3}>
              <Button href="/ListadoPedidos" className="btn btn-danger" size="sm">Regresar</Button>{" "}
              {generando ? (
                <Button className="btn btn-success" size="sm" disabled>Generando...</Button>
              ) : (
                <Button className="btn btn-success" size="sm" onClick={generar}>Generar {nOCs || ""} PO</Button>
              )}
            </Col>
            <Col md={3}>
              <Label>Asignar proveedor a todos</Label>
              <Input type="select" bsSize="sm" value={provTodos} onChange={(e) => asignaATodos(e.target.value)}>
                <option value="">Selecciona</option>
                {proveedores.sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))
                  .map((p) => (<option key={p._id} value={p._id}>{p.nombre_comercial}</option>))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Moneda</Label>
              <Input type="select" bsSize="sm" value={moneda} onChange={(e) => setMoneda(e.target.value)}>
                <option value="USD">USD</option>
                <option value="CNY">CNY (Yuanes)</option>
                <option value="MXN">MXN</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label>Plazo (dias)</Label>
              <Input type="number" bsSize="sm" value={plazo} onChange={(e) => setPlazo(e.target.value)} />
            </Col>
            <Col md={2}>
              <Label>Fecha compromiso</Label>
              <Input type="date" bsSize="sm" value={fechaCompromiso}
                onChange={(e) => { const v = e.target.value; compromisoATodos(v); }} />
              <small style={{ color: "#64748b" }}>Se copia a todos los articulos; cada uno se puede cambiar.</small>
            </Col>
          </Row>

          <h3 align="center">Generar PO</h3>
          <p align="center" style={{ color: "#64748b", fontSize: "0.8rem", marginBottom: 10 }}>
            Pedido <b>{encabezado.numero}</b> · {encabezado.cliente} · {renglones.length} renglon(es).
            Se genera una orden por cada proveedor distinto.
          </p>

          <div style={{ width: "100%", overflowX: "auto" }}>
            <Table size="sm" striped bordered>
              <thead>
                <tr>
                  <th style={{ width: 34 }}></th>
                  <th>Articulo</th>
                  <th style={{ textAlign: "right" }}>Pedida</th>
                  <th style={{ minWidth: 120 }}>A comprar</th>
                  <th style={{ minWidth: 110 }}>Costo ({moneda})</th>
                  <th style={{ minWidth: 150 }}>Fecha compromiso</th>
                  <th style={{ minWidth: 190 }}>Proveedor</th>
                </tr>
              </thead>
              <tbody>
                {articulos.map((a) => (
                  <React.Fragment key={a.grupo}>
                    <tr style={a.proveedor ? undefined : { background: "#fffbeb" }}>
                      <td className="text-center">
                        <Button size="sm" color="link" style={{ padding: 0 }}
                          title={abiertos[a.grupo] ? "Cerrar el modelo" : "Abrir el modelo por color y talla"}
                          onClick={() => setAbiertos((p) => ({ ...p, [a.grupo]: !p[a.grupo] }))}>
                          <i className={abiertos[a.grupo] ? "fas fa-chevron-down" : "fas fa-chevron-right"} />
                        </Button>
                      </td>
                      <td>
                        <b>{a.articulo}</b>{" "}
                        {repetidos[a.articulos] > 1 ? (
                          <span style={{ fontSize: 11, color: "#64748b" }}>· renglón {a.renglon}{" "}</span>
                        ) : null}
                        <small style={{ color: "#94a3b8" }}>{a.variantes.length} variante(s)</small>
                      </td>
                      <td style={{ textAlign: "right" }}>{new Intl.NumberFormat("es-MX").format(a.pedida)}</td>
                      <td>
                        {/* al tocar la cantidad se abre el modelo para repartirla por variante */}
                        <Input bsSize="sm" type="number" value={a.cantidad} readOnly
                          style={{ cursor: "pointer", background: "#f8fafc" }}
                          title="Ábrelo para repartir la cantidad por color y talla"
                          onFocus={() => setAbiertos((p) => ({ ...p, [a.grupo]: true }))}
                          onClick={() => setAbiertos((p) => ({ ...p, [a.grupo]: true }))} />
                      </td>
                      <td>
                        <Input bsSize="sm" type="number" min="0" step="any" value={a.precio}
                          onChange={(e) => { const v = e.target.value; cambiaArticulo(a.grupo, "precio", v); }} />
                      </td>
                      <td>
                        <Input bsSize="sm" type="date" value={a.compromiso}
                          onChange={(e) => { const v = e.target.value; cambiaArticulo(a.grupo, "compromiso", v); }} />
                      </td>
                      <td>
                        <Input bsSize="sm" type="select" value={a.proveedor}
                          onChange={(e) => { const v = e.target.value; cambiaArticulo(a.grupo, "proveedor", v); }}>
                          <option value="">Sin proveedor</option>
                          {proveedores.sort((x, y) => (x.nombre_comercial > y.nombre_comercial ? 1 : -1))
                            .map((p) => (<option key={p._id} value={p._id}>{p.nombre_comercial}</option>))}
                        </Input>
                      </td>
                    </tr>
                    {abiertos[a.grupo] ? a.variantes.map((r) => (
                      <tr key={r._id} style={{ background: "#f8fafc" }}>
                        <td></td>
                        <td style={{ paddingLeft: 26, fontSize: "0.82rem" }}>
                          {r.color || "Sin color"} · {r.talla || "Sin talla"}
                        </td>
                        <td style={{ textAlign: "right" }}>{new Intl.NumberFormat("es-MX").format(r.pedida)}</td>
                        <td>
                          <Input bsSize="sm" type="number" min="0" value={r.cantidad}
                            onChange={(e) => { const v = e.target.value; cambia(r._id, "cantidad", v); }} />
                        </td>
                        <td colSpan={3} style={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                          costo, fecha y proveedor se toman del modelo
                        </td>
                      </tr>
                    )) : null}
                  </React.Fragment>
                ))}
                {renglones.length === 0 ? (
                  <tr><td colSpan={7} align="center" style={{ color: "#94a3b8" }}>Este pedido no tiene renglones activos.</td></tr>
                ) : null}
              </tbody>
            </Table>
          </div>
          <p style={{ color: "#64748b", fontSize: "0.8rem" }}>
            Los renglones en ambar todavia no tienen proveedor y no se van a incluir.
          </p>
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default GenerarOCPedido;
